import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSettings } from '../lib/api';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Phone,
  Shield,
  Settings,
  LogOut,
  ChevronRight,
  Archive,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Daily Dashboard' },
  { path: '/funded', icon: CheckCircle, label: 'Funded Deals' },
  { path: '/unwinds', icon: XCircle, label: 'Unwinds' },
  { path: '/stip-tracker', icon: ClipboardList, label: 'Missing Stip Tracker' },
  { path: '/interviews', icon: Phone, label: 'Interview Concierge' },
  { path: '/compliance', icon: Shield, label: 'Compliance & Activity' },
  { path: '/archived', icon: Archive, label: 'Archived Deals' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    getSettings().then(res => {
      if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
    }).catch(() => {});
  }, []);

  // Listen for logo updates from Settings page
  useEffect(() => {
    const handler = (e) => setLogoUrl(e.detail);
    window.addEventListener('logo-updated', handler);
    return () => window.removeEventListener('logo-updated', handler);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 text-white flex flex-col z-40" style={{ backgroundColor: '#000000' }} data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Dealership Logo"
            className="max-h-12 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        ) : (
          <h1 className="font-heading text-2xl font-bold tracking-tight uppercase" data-testid="sidebar-title">
            Dealer Wingman
          </h1>
        )}
        <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest">
          Deal Command Center
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
            >
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
              {isActive && <ChevronRight size={16} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-slate-800 flex items-center justify-center">
            <span className="text-sm font-bold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-slate-400 truncate capitalize">
              {user?.role?.replace('_', ' ') || 'Staff'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <LogOut size={16} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};
